import { useEffect, useState } from "react";
import { useProducts } from "../../utils/hooks/useProducts";
import { toast } from "sonner";
import ProductCard from "../../components/productCard";

function ListingService() {
  const { data: products = [], isLoading, error } = useProducts();
  const [filters, setFilters] = useState({
    category: "",
    rating: 0,
    priceRange: [0, 1000],
    amenities: [],
  });

  useEffect(() => {
    if (error) {
      toast.error(`Error fetching products: ${error.message}`);
    } else if (!isLoading && products.length > 0) {
      toast.success("Products loaded successfully!");
    }
  }, [isLoading, error, products]);

  const handleFilterChange = (filterType: string, value: any) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const filteredProducts = products.filter((product: any) => {
    return (
      (!filters.category || product.category === filters.category) &&
      (!filters.rating || product.rating >= filters.rating) &&
      product.price >= filters.priceRange[0] &&
      product.price <= filters.priceRange[1] &&
      (!filters.amenities.length ||
        filters.amenities.every((amenity: string) =>
          product.amenities.includes(amenity)
        ))
    );
  });

  return (
    <div className="p-4 mx-6">
      <div className="flex gap-6">
        {/* Filters Section */}
        <div className="w-1/4 space-y-6 border p-4">
          {/* Category Filter */}
          <div>
            <h3 className="font-bold mb-2">Category</h3>
            <div className="space-y-2">
              {["", "electronics", "furniture", "clothing"].map((category) => (
                <label key={category} className="flex items-center">
                  <input
                    type="radio"
                    name="category"
                    value={category}
                    checked={filters.category === category}
                    onChange={(e) =>
                      handleFilterChange("category", e.target.value)
                    }
                    className="mr-2 color-primary"
                  />
                  <span>
                    {category
                      ? category.charAt(0).toUpperCase() + category.slice(1)
                      : "All"}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Price Filter */}
          <div>
            <h3 className="font-bold mb-2">Price Filter</h3>
            <input
              type="range"
              min="0"
              max="1000"
              step="10"
              value={filters.priceRange[1]}
              onChange={(e) =>
                handleFilterChange("priceRange", [0, Number(e.target.value)])
              }
            />
            <p className="text-sm">Up to ${filters.priceRange[1]}</p>
          </div>

          {/* Rating Filter */}
          <div>
            <h3 className="font-bold mb-2">Rating</h3>
            <div className="space-y-2">
              {[
                { value: 0, label: "All" },
                { value: 4, label: "4 Stars & Up" },
                { value: 3, label: "3 Stars & Up" },
                { value: 2, label: "2 Stars & Up" },
                { value: 1, label: "1 Star & Up" },
              ].map((rating) => (
                <label key={rating.value} className="flex items-center">
                  <input
                    type="radio"
                    name="rating"
                    value={rating.value}
                    checked={filters.rating === rating.value}
                    onChange={(e) =>
                      handleFilterChange("rating", Number(e.target.value))
                    }
                    className="mr-2"
                  />
                  <span>{rating.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Amenities Filter */}
          <div>
            <h3 className="font-bold mb-2">Amenities</h3>
            <div>
              <label>
                <input
                  type="checkbox"
                  value="wifi"
                  onChange={(e) =>
                    handleFilterChange("amenities", [
                      ...filters.amenities,
                      e.target.value,
                    ])
                  }
                />
                <span className="ml-2">Wi-Fi</span>
              </label>
            </div>
            <div>
              <label>
                <input
                  type="checkbox"
                  value="parking"
                  onChange={(e) =>
                    handleFilterChange("amenities", [
                      ...filters.amenities,
                      e.target.value,
                    ])
                  }
                />
                <span className="ml-2">Parking</span>
              </label>
            </div>
          </div>
        </div>

        {/* Products Listing */}
        <div className="w-3/4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {isLoading ? (
              <p>Loading...</p>
            ) : filteredProducts.length > 0 ? (
              filteredProducts.map((product: any) => (
                <ProductCard key={product.id} singleProduct={product} />
              ))
            ) : (
              <p>No products found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ListingService;
